import { Box, Button, Drawer, Typography } from "@material-ui/core";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../epocus-palette";
import type { CptSuggestion, IcdSuggestion } from "./types";

const MODIFIER_LABELS: Record<string, string> = {
  "-26": "Professional component — interpreted by physician",
  "-TC": "Technical component — equipment and technician only",
};

const formatModifier = (modifier: string): string => {
  const label = MODIFIER_LABELS[modifier];
  return label ? `${modifier} — ${label}` : modifier;
};

interface Props {
  open: boolean;
  onClose: () => void;
  cptSuggestions: CptSuggestion[];
  icd: IcdSuggestion;
  onConfirm: () => void;
  onOverride: () => void;
  readOnly?: boolean;
  isSubmitting?: boolean;
}

export const BillingCptConfirmationSheet: FC<Props> = ({
  open,
  onClose,
  cptSuggestions,
  icd,
  onConfirm,
  onOverride,
  readOnly = false,
  isSubmitting = false,
}) => {
  const primary = cptSuggestions[0];
  const additionalCodes = cptSuggestions.slice(1);
  const payerRiskNote = cptSuggestions.find(
    (s) => s.payerRiskNote,
  )?.payerRiskNote;
  const completenessWarning = cptSuggestions.find(
    (s) => s.completenessWarning,
  )?.completenessWarning;

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        style: { backgroundColor: "transparent", boxShadow: "none" },
      }}
    >
      <Box
        style={{
          borderRadius: "16px 16px 0 0",
          padding: "12px 24px 32px",
          backgroundColor: EPOCUS_WHITE,
          maxWidth: 680,
          margin: "0 auto",
          width: "100%",
          boxSizing: "border-box",
        }}
      >
        {/* Handle bar */}
        <Box
          style={{
            width: 40,
            height: 4,
            backgroundColor: EPOCUS_BORDER,
            borderRadius: 2,
            margin: "0 auto 20px",
          }}
        />

        {/* Sheet heading */}
        <Typography
          style={{
            fontSize: rem(16),
            fontWeight: 700,
            color: EPOCUS_TEXT_PRIMARY,
            marginBottom: 20,
          }}
        >
          Generated billing code{cptSuggestions.length > 1 ? "s" : ""}
        </Typography>

        {primary && (
          <>
            {/* Procedure code label */}
            <Typography
              style={{
                fontSize: rem(10),
                fontWeight: 700,
                color: EPOCUS_TEXT_SECONDARY,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 4,
              }}
            >
              Procedure code
            </Typography>

            {/* Description + CPT code — inline on wide screens, stacked on narrow */}
            <Box
              style={{
                display: "flex",
                flexWrap: "wrap",
                alignItems: "baseline",
                gap: "0 8px",
                marginBottom: 8,
              }}
            >
              <Typography
                style={{
                  fontSize: rem(24),
                  fontWeight: 700,
                  color: EPOCUS_TEXT_PRIMARY,
                  lineHeight: 1.2,
                }}
              >
                {primary.description}
              </Typography>
              <Typography
                style={{
                  fontSize: rem(14),
                  color: EPOCUS_TEXT_SECONDARY,
                }}
              >
                CPT {primary.code}
              </Typography>
            </Box>

            {/* Modifier */}
            {primary.modifier !== null && (
              <Box style={{ marginBottom: 8 }}>
                <Typography
                  style={{
                    fontSize: rem(10),
                    fontWeight: 700,
                    color: EPOCUS_TEXT_SECONDARY,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 2,
                  }}
                >
                  Modifier
                </Typography>
                <Typography
                  style={{ fontSize: rem(14), color: EPOCUS_TEXT_SECONDARY }}
                >
                  {formatModifier(primary.modifier)}
                </Typography>
              </Box>
            )}

            {/* Units — only shown when > 1 (e.g. 51798 bladder pre+post-void) */}
            {primary.units !== undefined && primary.units > 1 && (
              <Box style={{ marginBottom: 8 }}>
                <Typography
                  style={{
                    fontSize: rem(10),
                    fontWeight: 700,
                    color: EPOCUS_TEXT_SECONDARY,
                    textTransform: "uppercase",
                    letterSpacing: "0.08em",
                    marginBottom: 2,
                  }}
                >
                  Units
                </Typography>
                <Typography
                  style={{ fontSize: rem(16), color: EPOCUS_TEXT_SECONDARY }}
                >
                  {primary.units}
                </Typography>
              </Box>
            )}
          </>
        )}

        {/* Additional codes (Doppler add-ons, eFAST components) */}
        {additionalCodes.length > 0 && (
          <Box style={{ marginBottom: 20 }}>
            {additionalCodes.map((s) => (
              <Box
                key={s.code}
                style={{
                  backgroundColor: EPOCUS_BG,
                  border: `1px solid ${EPOCUS_BORDER}`,
                  borderRadius: 8,
                  padding: "8px 12px",
                  marginBottom: 6,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography
                    style={{
                      fontSize: rem(14),
                      fontWeight: 700,
                      color: EPOCUS_PRIMARY,
                    }}
                  >
                    {s.description}
                  </Typography>
                  <Typography
                    style={{ fontSize: rem(12), color: EPOCUS_TEXT_SECONDARY }}
                  >
                    CPT {s.code}
                    {s.modifier ? ` · ${formatModifier(s.modifier)}` : ""}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Advisory notes */}
        {(completenessWarning ?? payerRiskNote) && (
          <Box
            style={{
              backgroundColor: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 16,
            }}
          >
            {completenessWarning && (
              <Typography
                style={{
                  fontSize: rem(12),
                  color: "#92400e",
                  marginBottom: payerRiskNote ? 6 : 0,
                }}
              >
                ⚠️ {completenessWarning}
              </Typography>
            )}
            {payerRiskNote && (
              <Typography style={{ fontSize: rem(12), color: "#92400e" }}>
                ℹ️ {payerRiskNote}
              </Typography>
            )}
          </Box>
        )}

        {/* ICD-10 box */}
        <Box
          style={{
            backgroundColor: EPOCUS_BG,
            border: `1px solid ${EPOCUS_BORDER}`,
            borderRadius: 8,
            padding: "12px 16px",
            marginBottom: 28,
          }}
        >
          <Typography
            style={{
              fontSize: rem(10),
              fontWeight: 700,
              color: EPOCUS_TEXT_SECONDARY,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              marginBottom: 4,
            }}
          >
            ICD-10
          </Typography>
          <Typography
            style={{
              fontSize: rem(14),
              fontWeight: 500,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            {icd.code} — {icd.description}
          </Typography>
        </Box>

        {/* Billing-blocked state — no codes from engine */}
        {!primary && !readOnly && (
          <Box
            style={{
              backgroundColor: "#fef2f2",
              border: "1px solid #fca5a5",
              borderRadius: 8,
              padding: "10px 12px",
              marginBottom: 16,
            }}
          >
            <Typography style={{ fontSize: rem(13), color: "#991b1b" }}>
              Billing is blocked — complete the required worksheet fields (e.g.
              view count, measurements) before confirming.
            </Typography>
          </Box>
        )}

        {/* Confirm button */}
        <Button
          variant="contained"
          fullWidth
          onClick={onConfirm}
          disabled={!primary || readOnly || isSubmitting}
          disableElevation
          style={{
            backgroundColor:
              !primary || readOnly || isSubmitting ? undefined : EPOCUS_PRIMARY,
            color: !primary || readOnly || isSubmitting ? undefined : "#fff",
            borderRadius: 10,
            padding: "14px 0",
            fontSize: rem(16),
            fontWeight: 600,
            textTransform: "none",
            marginBottom: 8,
            minHeight: 52,
          }}
        >
          {readOnly
            ? "Billing record locked"
            : isSubmitting
              ? "Submitting…"
              : "Confirm & Submit"}
        </Button>

        {/* Override link — hidden once locked */}
        {!readOnly && (
          <Button
            fullWidth
            onClick={onOverride}
            style={{
              color: EPOCUS_TEXT_SECONDARY,
              fontSize: rem(14),
              textTransform: "none",
              minHeight: 44,
            }}
          >
            {cptSuggestions.length > 1
              ? "Override suggested codes ›"
              : "Override suggested code ›"}
          </Button>
        )}
      </Box>
    </Drawer>
  );
};
